import { describeUnknownError } from './errors';
import { flush, log } from './logger';
import { recorder } from './recorder';
import { installSeedTemplates } from './seedTemplates';
import { refreshWakeLock } from './wakeLock';
import type { WakeLockManagerLike } from './wakeLock';
import { claimWriter, refreshWriterLock, status } from './writerLock';
import type { WriterLockManager, WriterStatus } from './writerLock';

/**
 * **The wiring U-4 left for the first unit with a screen** — §13.7's lock claim, plus PRE-2's
 * first-run seed install.
 *
 * U-4 built the writer lock and had no screen to claim it from, so it left an alarm instead of a
 * comment: `assertClaimed()` raises an OBS-1 `error` **once** if a command is ever dispatched
 * without a claim. This module is what discharges that obligation. Under the re-decided §13.7 the
 * claim no longer decides whether commands work — it decides whether a **banner** can ever appear,
 * which is a thing the user silently loses rather than a thing that fails loudly.
 *
 * ── Claim, then re-attempt on `visibilitychange` ─────────────────────────────
 *
 * A Web Lock is released by the browser when the page is destroyed and is **not** released on
 * backgrounding, so there is nothing to refresh and no heartbeat to keep alive — that whole
 * mechanism is withdrawn. What remains worth doing on `visibilitychange` is the opposite direction:
 * a context that did **not** get the lock asks again, because the other window may have been closed
 * while this one was hidden, and a banner that outlives the window it names is the defect §13.7 was
 * re-decided to remove. {@link refreshWriterLock} is a no-op for the holder, so the listener costs
 * nothing in the state where it would be wrong to act.
 *
 * ── One listener, two jobs (§2.6) ────────────────────────────────────────────
 *
 * ENV-8's re-acquisition rides the **same** listener: *"This is the same listener `bootstrap.ts`
 * already owns for §13.7's writer-lock re-attempt, so it is one listener with two jobs, not a second
 * lifecycle mechanism."* Three properties of that arrangement are load-bearing and each is asserted
 * in `bootstrap.test.ts`:
 *
 * 1. **It fires only on the way to visible.** The handler previously ran on both edges. That was
 *    merely wasteful for the writer lock and would be actively wrong for the wake lock — a request
 *    from a hidden page rejects, which is the retry NFR-5 bans.
 * 2. **`pageshow` is wired too**, for §13.7's stated reason: it *"covers bfcache restore, where
 *    WebKit can resume a page without a `visibilitychange` — the one iOS path that would otherwise
 *    skip the re-attempt."* A resumed page that skipped re-acquisition would sit through the second
 *    half with the screen sleeping, so the wake lock rides it on the same argument. This is an
 *    extension of §2.6, which names only `visibilitychange`; it introduces no cadence and no timer,
 *    and both events are user-driven resumes.
 * 3. **Neither job is a poll.** No interval, no timeout, no repeated request. §13.7 and NFR-5 both
 *    forbid one, and `bootstrap.test.ts` asserts the request counts rather than the intent.
 *
 * ── Two hosts, not one, and this was a live defect ───────────────────────────
 *
 * The page-transition events are registered on **`window`** and `visibilitychange` on **`document`**.
 * That is not interchangeable and it is why {@link BootstrapOptions} now carries two targets. This
 * module previously registered `pageshow` on the same `document` target as `visibilitychange`, and
 * **that listener never ran in a browser**: page transition events are fired *at* `Window`, which is
 * the top of the propagation path, so a `document` listener is never on it. Verified against WebKit
 * — both a synthetic dispatch at `window` and the browser's own load-time `pageshow` reach only
 * `window` listeners. The unit tests did not notice because they inject one `EventTarget` and attach
 * to it, so the targeting question never arose. Consequence while it stood: §13.7's re-attempt and
 * §2.6's re-acquisition were absent on exactly the bfcache path they were added for.
 *
 * ── The OBS-1 lifecycle flush (§12.1a) rides the same pair ───────────────────
 *
 * §12.1a: *"add the `visibilitychange → hidden` and `pagehide` flush … touches the logger plus the
 * listener pair `bootstrap.ts` already owns."* Backgrounding is when jetsam probability jumps, so it
 * is the moment to drain the buffer — up to `LOG_FLUSH_THRESHOLD − 1` entries that a hard kill would
 * otherwise take, and specifically the most recent ones, which are the ones describing what led to
 * the kill. Until this landed the size threshold was the **only** live trigger and a full WebKit walk
 * produced an empty `logEntries`.
 *
 * **`pagehide` is a flush trigger even though §13.7 refuses it as a lock-release trigger**, and that
 * is not an inconsistency: §13.7 rejects it because iOS fires it on backgrounding and *releasing*
 * then would be wrong. Flushing then is exactly right. The same platform fact points opposite ways
 * for two different operations, so it is stated at each use.
 *
 * ── Nothing here may take the app down ───────────────────────────────────────
 *
 * NFR-8 forbids a silent catch, not a handled one. Every step reports its own failure through OBS-1
 * **and** returns it on the report, because the alternative — letting a storage fault reject out of
 * `bootstrapApp` — puts a blank screen in front of the user at a venue for a condition the app can
 * run through. The lock claim failing means §13.7 is not held; that is a missing banner, not a
 * shutdown.
 */

const MODULE = 'bootstrap';

/** The human-readable half of a failure. `describeUnknownError` carries the structured half. */
function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export interface BootstrapReport {
  /** §13.7 — the state this context ended up in, or `null` if the claim itself failed. */
  readonly writer: WriterStatus | null;
  /** PRE-2 — whether the seeded library is present. `false` means the library may be incomplete. */
  readonly seedsInstalled: boolean;
  /**
   * §8.5 — recordings a previous launch never finalized, assembled from the chunks that survived.
   *
   * Non-zero means the app was killed mid-recording and the audio was rescued. Reported rather than
   * silent because it is the one number that tells the user's future self how often a hard kill has
   * actually happened at a venue.
   */
  readonly recoveredRecordings: number;
  /** Every failure, in order. Empty is the normal case; NFR-8 forbids losing any of them. */
  readonly errors: readonly string[];
  /** Removes every lifecycle listener this bootstrap installed. Idempotent. */
  readonly dispose: () => void;
}

export interface BootstrapOptions {
  /** Wall clock, injected — it is recorded on the diagnostic row, never compared against. */
  readonly now?: () => number;
  /**
   * What the `visibilitychange` listener is attached to. Defaults to `document`, which is the only
   * correct answer in a browser and the reason it is a parameter rather than a hard reference: the
   * Node suite has no `document`, and a bootstrap whose one wiring obligation cannot be exercised by
   * a test is a bootstrap whose omission the next unit would rediscover in the field.
   */
  readonly visibilityTarget?: EventTarget;
  /**
   * What `pageshow` and `pagehide` are attached to. Defaults to `window`.
   *
   * **Separate from `visibilityTarget` because the correct host is different**, not for symmetry —
   * see the module note. Attaching a page-transition event to `document` produces a listener that
   * never runs, which is precisely the defect this parameter's introduction fixes.
   */
  readonly pageTarget?: EventTarget;
  /**
   * §13.7's lock manager. Omitted takes `navigator.locks`; `null` runs unlocked.
   *
   * Forwarded rather than reached for, for the same reason `writerLock` takes it as a value: the
   * Node suite has no `navigator`, and the open question of whether an installed PWA and a Safari
   * tab share one manager is a question about which manager answers.
   */
  readonly locks?: WriterLockManager | null;
  /** ENV-8's manager. Omitted takes `navigator.wakeLock`; `null` runs the app unlocked. */
  readonly wakeLock?: WakeLockManagerLike | null;
  /**
   * Whether the page is currently visible.
   *
   * A parameter for the same reason `visibilityTarget` is one — the Node suite has no `document` —
   * and for a second reason that is the point of the gate: the negative *"a `visibilitychange`
   * outside in-match states requests nothing"* is only assertable if a test can present a **hidden**
   * page, which no `EventTarget` alone can do.
   */
  readonly isVisible?: () => boolean;
}

/** Visible unless a real `document` says otherwise. A missing document is not a hidden page. */
function documentIsVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/**
 * Claims the writer lock, wires the re-attempt, and installs the seeded library.
 *
 * Called once, from `main.tsx`, **before** any screen can dispatch. Order is load-bearing: the seed
 * install is itself a command, so it must run after the claim or it would be the very dispatch
 * `assertClaimed()` exists to complain about.
 */
export async function bootstrapApp(options: BootstrapOptions = {}): Promise<BootstrapReport> {
  const now = options.now ?? (() => Date.now());
  const target = options.visibilityTarget ?? document;
  const pageTarget = options.pageTarget ?? window;
  const isVisible = options.isVisible ?? documentIsVisible;
  const lockOptions = 'locks' in options ? { locks: options.locks } : {};
  const wakeOptions = 'wakeLock' in options ? { wakeLock: options.wakeLock } : {};
  const errors: string[] = [];

  /**
   * The one handler, both jobs, on the way to **visible** only.
   *
   * Fire-and-forget: a `visibilitychange` handler has nobody to return a promise to, and an
   * unhandled rejection here would reach OBS-1's global handler as an anonymous fault rather than as
   * the named condition it is. The two jobs are independent — a failed writer re-attempt must not
   * cost the screen its wake lock — so each carries its own catch.
   */
  const onResume = (): void => {
    if (!isVisible()) return;

    void refreshWriterLock(now(), lockOptions).catch((cause: unknown) => {
      log.error(MODULE, '§13.7: the writer lock could not be re-attempted', {
        error: describeUnknownError(cause),
      });
    });

    // §2.6 — gated on `isInMatch` inside `refreshWakeLock`, which is where the state lives. A
    // request from here in `preMatch` or `fullTime` would be the negative ENV-8 forbids.
    void refreshWakeLock(wakeOptions).catch((cause: unknown) => {
      log.error(MODULE, 'ENV-8: the screen wake lock could not be re-acquired', {
        error: describeUnknownError(cause),
      });
    });

    /**
     * §8.1 — the third job on this listener (build unit U-11).
     *
     * *"A `track.readyState` guard re-acquires the stream on `visibilitychange → visible` and after
     * an interruption, so a post-phone-call recording does not silently capture nothing."* It is
     * here rather than in a listener of its own for the reason stated at the top of this module: one
     * listener with N jobs is not a new lifecycle mechanism, and NFR-5 forbids one. Gated on
     * `isInMatch` inside `refreshMicrophone`, exactly as the wake lock is, and a no-op when a
     * healthy stream is already held — so a resume that changed nothing requests nothing.
     *
     * Synchronous and self-reporting, so there is no promise here to leave unhandled.
     */
    recorder.refreshMicrophone();
  };

  /**
   * §12.1a — the OBS-1 flush at the background boundary.
   *
   * Fire-and-forget for the same reason `onResume` is: a lifecycle handler has nobody to return a
   * promise to. `flush()` never rejects — it is the single sanctioned NFR-8 exception, reporting its
   * own persistence failure to the console and dropping the batch — so there is deliberately no
   * `.catch` here to imply otherwise, and nothing on the capture path can be taken down by it.
   *
   * Not gated on visibility: `pagehide` is not a visibility question, and `visibilitychange` reaches
   * it only on the edge that has already been tested as hidden.
   */
  const onSuspend = (): void => {
    void flush();
  };

  // One `visibilitychange` listener, both edges: toward visible it resumes, toward hidden it
  // flushes. Two listeners on one event would be two things to keep in step for no gain.
  const onVisibilityChange = (): void => {
    if (isVisible()) onResume();
    else onSuspend();
  };

  /**
   * **Registered before the first `await` in this function, and that ordering is load-bearing.**
   *
   * These three listeners used to be attached *after* `claimWriter()`, which is asynchronous. Every
   * lifecycle event that arrived while the claim was in flight was therefore dropped — and the one
   * that always arrives in that window is **`pageshow` at page load**, which is precisely the event
   * §2.6 relies on to re-acquire the wake lock after an SES-3 resume.
   *
   * Found by U-20's route table, and only reachable through it: before the shell existed, a resume
   * into an in-match state took a user tap on a switcher tab, so by the time the capture surface
   * mounted the load-time `pageshow` was long past and irrelevant. With the app opening straight
   * onto the surface §2.5 names, the mount and the `pageshow` are the same moment — and a
   * wake-lock request made that early is refused by WebKit (`NotAllowedError`), which left a
   * resumed half with the screen free to sleep for its whole length.
   *
   * Nothing about the listeners themselves changed. What changed is that a bootstrap can no longer
   * be racing them, which also restores §13.7's re-attempt and §8.1's microphone refresh on the
   * same window.
   */
  target.addEventListener('visibilitychange', onVisibilityChange);
  pageTarget.addEventListener('pageshow', onResume);
  pageTarget.addEventListener('pagehide', onSuspend);

  let writer: WriterStatus | null = null;
  try {
    writer = await claimWriter(now(), lockOptions);
  } catch (cause) {
    errors.push(messageOf(cause));
    log.error(MODULE, '§13.7: the writer lock could not be claimed; this context is unprotected', {
      error: describeUnknownError(cause),
    });
  }

  let seedsInstalled = false;
  try {
    const result = await installSeedTemplates();
    seedsInstalled = result.ok;
    if (!result.ok) {
      errors.push(result.message);
      log.error(MODULE, 'PRE-2: the seeded template library was not installed', {
        code: result.code,
        message: result.message,
      });
    }
  } catch (cause) {
    errors.push(messageOf(cause));
    log.error(MODULE, 'PRE-2: the seeded template library could not be installed', {
      error: describeUnknownError(cause),
    });
  }

  /**
   * §8.5 — the audio recovery pass, at launch (build unit U-11).
   *
   * *"Recovery runs on `pageshow`/resume, not `pagehide`: any `AudioBlob` with `complete=false` is
   * assembled and probed at launch. Worst-case loss is one timeslice, not the whole segment."*
   *
   * Here rather than on a screen because a hard jetsam kill can happen on any surface and the
   * recovery must not wait for the user to navigate back to the one that owns audio. It is also the
   * reason this runs unconditionally rather than only when a match is active: the rows it closes
   * belong to a match that may already have ended.
   *
   * A failure is recorded and never thrown. The chunks stay on disk with the row still incomplete,
   * so the *next* launch tries again — which is strictly better than taking the app down over an
   * assembly that can be retried.
   */
  let recoveredRecordings = 0;
  try {
    recoveredRecordings = await recorder.recoverIncompleteRecordings();
  } catch (cause) {
    errors.push(messageOf(cause));
    log.error(MODULE, '§8.5: the audio recovery pass failed; it will run again at the next launch', {
      error: describeUnknownError(cause),
    });
  }

  log.info(MODULE, 'bootstrap complete', {
    holdsWriterLock: (writer ?? status()).holdsLock,
    seedsInstalled,
    recoveredRecordings,
    errorCount: errors.length,
  });

  return {
    writer,
    seedsInstalled,
    recoveredRecordings,
    errors,
    dispose: () => {
      target.removeEventListener('visibilitychange', onVisibilityChange);
      pageTarget.removeEventListener('pageshow', onResume);
      pageTarget.removeEventListener('pagehide', onSuspend);
    },
  };
}
